import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import dayjs from "dayjs";

const UserContext = createContext();

function detectClientPlatform() {
  const ua = navigator?.userAgent || "";
  const isMobileUa =
    /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua);
  // iPadOS can present as Macintosh + touch.
  const isTouchMac =
    /Macintosh/i.test(ua) && (navigator?.maxTouchPoints || 0) > 1;
  return isMobileUa || isTouchMac ? "mobile" : "web";
}

async function getGeoSnapshot() {
  try {
    const res = await fetch("https://ipapi.co/json/", {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      city: data?.city || null,
      region: data?.region || null,
      country: data?.country_name || data?.country || null,
      countryCode: data?.country_code || null,
      timezone: data?.timezone || null,
      ip: data?.ip || null,
    };
  } catch (_err) {
    return null;
  }
}

export function UserProvider({ children }) {
  // undefined = loading, null = signed out, object = signed in
  const [user, setUser] = useState(undefined);
  // For a delegated team member this is the OWNER's uid; otherwise it equals
  // the signed-in user's own uid. All owner-scoped data queries should key off
  // effectiveUid. `null` while loading / signed out.
  const [effectiveUid, setEffectiveUid] = useState(null);
  const [isDelegate, setIsDelegate] = useState(false);
  const [currency, setCurrency] = useState("EUR");
  const [trial, setTrial] = useState({
    loading: true,
    expired: false,
    minutesLeft: 0,
    trialEndsAt: null,
  });

  // --- trial calc helper ---
  const computeTrial = (userData) => {
    const { trialEndsAt, plan } = userData || {};
    if (plan === "pro") {
      return {
        loading: false,
        expired: false,
        minutesLeft: 0,
        trialEndsAt: null,
      };
    }
    if (trialEndsAt) {
      const end =
        trialEndsAt.toDate?.() ??
        (typeof trialEndsAt === "string" ? new Date(trialEndsAt) : trialEndsAt);
      const now = dayjs();
      const endDayjs = dayjs(end);
      const expired = now.isAfter(endDayjs);
      const minutesLeft = expired
        ? 0
        : Math.max(endDayjs.diff(now, "minute", true), 0);
      return {
        loading: false,
        expired,
        minutesLeft: Math.ceil(minutesLeft),
        trialEndsAt: end,
      };
    }
    return {
      loading: false,
      expired: true,
      minutesLeft: 0,
      trialEndsAt: null,
    };
  };

  // Resolve the delegated-access claim for a firebase user.
  // Returns { actsForUid, delegate } — actsForUid falls back to the user's own
  // uid so callers can always query `users/{actsForUid}` for owner-scoped data.
  const resolveDelegation = async (firebaseUser) => {
    try {
      const tok = await firebaseUser.getIdTokenResult();
      const claimed = tok?.claims?.actsForUid || null;
      if (claimed && claimed !== firebaseUser.uid) {
        return { actsForUid: claimed, delegate: true };
      }
    } catch (e) {
      console.warn("resolveDelegation error:", e);
    }
    return { actsForUid: firebaseUser.uid, delegate: false };
  };

  // Build the context `user` object from the signed-in identity + the account
  // doc that drives entitlements. For a delegate, identity (uid/email) is their
  // own, but plan/trial/currency come from the OWNER's user doc.
  const applyUserDocs = useCallback(
    (firebaseUser, accountUid, accountData, delegate) => {
      const data = accountData || {};
      setEffectiveUid(accountUid);
      setIsDelegate(delegate);
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        ...data,
        // Keep identity fields anchored to the real signed-in user.
        uid_effective: accountUid,
        isDelegate: delegate,
      });
      setCurrency(data?.currency || "EUR");
      setTrial(computeTrial(data));
    },
    [],
  );

  // --- refreshUser: one-time refetch ---
  const refreshUser = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setUser(null);
      setEffectiveUid(null);
      setIsDelegate(false);
      setCurrency("EUR");
      setTrial({
        loading: false,
        expired: true,
        minutesLeft: 0,
        trialEndsAt: null,
      });
      return;
    }

    try {
      // force token refresh (rules sometimes check token.email / actsForUid)
      await firebaseUser.getIdToken(true);
    } catch (e) {
      console.warn("refreshUser getIdToken error:", e);
    }

    try {
      const { actsForUid, delegate } = await resolveDelegation(firebaseUser);
      const snap = await getDoc(doc(db, "users", actsForUid));
      if (snap.exists()) {
        applyUserDocs(firebaseUser, actsForUid, snap.data(), delegate);
      } else {
        // no account doc yet → minimal info
        applyUserDocs(firebaseUser, actsForUid, null, delegate);
        setTrial({
          loading: false,
          expired: true,
          minutesLeft: 0,
          trialEndsAt: null,
        });
      }
    } catch (e) {
      console.warn("refreshUser getDoc error:", e);
    }
  }, [applyUserDocs]);

  useEffect(() => {
    // Safari restores pages from bfcache in a frozen state — onAuthStateChanged
    // does NOT re-fire on thaw. When the page is restored (event.persisted===true)
    // kick the user state back to undefined so AppRoutes shows the spinner,
    // then re-read the current auth state manually.
    const handlePageShow = (e) => {
      if (e.persisted) {
        setUser(undefined);
        const current = auth.currentUser;
        if (current === null) {
          setUser(null);
          setEffectiveUid(null);
          setIsDelegate(false);
        } else if (current) {
          // We have a user — re-resolve delegation and re-fetch the account doc
          // so the UI unfreezes correctly.
          resolveDelegation(current)
            .then(({ actsForUid, delegate }) =>
              getDoc(doc(db, "users", actsForUid)).then((snap) => {
                if (snap.exists()) {
                  applyUserDocs(current, actsForUid, snap.data(), delegate);
                } else {
                  applyUserDocs(current, actsForUid, null, delegate);
                  setTrial({ loading: false, expired: true, minutesLeft: 0, trialEndsAt: null });
                }
              }),
            )
            .catch(() => {
              // If Firestore fails, at least let the auth state resolve to non-undefined
              setUser({ uid: current.uid, email: current.email });
              setEffectiveUid(current.uid);
            });
        }
        // If auth.currentUser is still undefined the normal onAuthStateChanged
        // will eventually fire and set user correctly.
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    setUser(undefined); // loading
    let unsubUserDoc = null;

    const unsubAuth = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        if (unsubUserDoc) unsubUserDoc();
        setUser(null);
        setEffectiveUid(null);
        setIsDelegate(false);
        setCurrency("EUR");
        setTrial({
          loading: false,
          expired: true,
          minutesLeft: 0,
          trialEndsAt: null,
        });
        return;
      }

      // Resolve delegated access; owner-scoped data (incl. this user doc listener)
      // keys off the effective (owner) uid, while lastActive telemetry is written
      // to the real signed-in user's own doc.
      const { actsForUid, delegate } = await resolveDelegation(firebaseUser);
      const userRef = doc(db, "users", firebaseUser.uid);
      const accountRef = doc(db, "users", actsForUid);
      const browserTz =
        Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      const browserLocale = navigator?.language || null;
      const browserUa = navigator?.userAgent || null;
      const clientPlatform = detectClientPlatform();

      // Update lastActiveAt (fire-and-forget)
      updateDoc(userRef, {
        lastActiveAt: serverTimestamp(),
        lastActivePlatform: clientPlatform,
        lastLoginTimezone: browserTz,
        lastLoginLocale: browserLocale,
        "lastLoginMeta.platform": clientPlatform,
        "lastLoginMeta.locale": browserLocale,
        "lastLoginMeta.timezone": browserTz,
        "lastLoginMeta.userAgent": browserUa,
        "lastLoginMeta.updatedAt": serverTimestamp(),
      }).catch((err) => console.warn("Failed to update lastActiveAt:", err));

      // Best-effort geo enrichment for founder CRM login location visibility.
      getGeoSnapshot().then((geo) => {
        if (!geo) return;
        updateDoc(userRef, {
          lastLoginCity: geo.city,
          lastLoginRegion: geo.region,
          lastLoginCountry: geo.country,
          lastLoginCountryCode: geo.countryCode,
          lastLoginTimezone: geo.timezone || browserTz,
          lastLoginIp: geo.ip,
          "lastLoginMeta.city": geo.city,
          "lastLoginMeta.region": geo.region,
          "lastLoginMeta.country": geo.country,
          "lastLoginMeta.countryCode": geo.countryCode,
          "lastLoginMeta.timezone": geo.timezone || browserTz,
          "lastLoginMeta.ip": geo.ip,
          "lastLoginMeta.updatedAt": serverTimestamp(),
        }).catch((err) =>
          console.warn("Failed to update lastLogin location:", err),
        );
      });

      // optional: one-time fetch for immediate data (from the account/owner doc)
      try {
        const snap = await getDoc(accountRef);
        if (snap.exists()) {
          applyUserDocs(firebaseUser, actsForUid, snap.data(), delegate);
        } else {
          applyUserDocs(firebaseUser, actsForUid, null, delegate);
          setTrial({
            loading: false,
            expired: true,
            minutesLeft: 0,
            trialEndsAt: null,
          });
        }
      } catch (e) {
        if (e?.code === "permission-denied") {
          // Token/rules race — let the live snapshot below pick it up shortly.
          console.warn(
            "UserContext: transient permission-denied on first getDoc (ignoring).",
          );
        } else {
          console.warn("Initial getDoc error:", e);
        }
      }

      // live listener on the account/owner doc
      unsubUserDoc = onSnapshot(
        accountRef,
        (snap) => {
          if (snap.exists()) {
            applyUserDocs(firebaseUser, actsForUid, snap.data(), delegate);
          }
        },
        (err) => {
          console.warn("User doc snapshot error:", err);
        },
      );
    });

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      if (unsubUserDoc) unsubUserDoc();
      unsubAuth();
    };
  }, [refreshUser, applyUserDocs]);

  return (
    <UserContext.Provider
      value={{ user, effectiveUid, isDelegate, currency, trial, refreshUser }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}

// Convenience: the uid that owns the data this session operates on.
// Equals the signed-in user's own uid unless they are a delegated team member,
// in which case it is the OWNER's uid. Use this for Firestore data queries
// (groomerId/userId/ownerId filters and writes). For identity — backend calls,
// per-user localStorage keys, payment identity — use `user.uid` instead.
export function useAccountUid() {
  const { effectiveUid, user } = useContext(UserContext);
  return effectiveUid || user?.uid || null;
}
